import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import "./mobile-app-development.css";

type CardItem = {
  title: string;
  text?: string;
  icon?: string;
  iconUrl?: string;
};

type TechItem = {
  name: string;
  logo: string;
  group?: string;
};

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const trustStats = [
  { value: "500+", label: "Apps Delivered" },
  { value: "100+", label: "Happy Clients" },
  { value: "10+", label: "Years Experience" },
  { value: "4.9", label: "Client Rating" },
];

const services: CardItem[] = [
  {
    title: "iOS App Development",
    text: "Native iOS apps built with Swift for performance, security, and App Store success.",
    iconUrl: "https://cdn.simpleicons.org/apple/111111",
  },
  {
    title: "Android App Development",
    text: "Robust Android applications optimized for every device and Google Play standards.",
    iconUrl: "https://cdn.simpleicons.org/android/3DDC84",
  },
  {
    title: "Cross Platform Apps",
    text: "Single codebase apps that deliver native-like experiences on iOS and Android.",
    iconUrl: "https://cdn.simpleicons.org/expo/000020",
  },
  {
    title: "Flutter Development",
    text: "Beautiful, fast cross-platform apps with Flutter's rich UI toolkit.",
    iconUrl: "https://cdn.simpleicons.org/flutter/02569B",
  },
  {
    title: "React Native Development",
    text: "Scalable mobile apps using React Native for rapid development cycles.",
    iconUrl: "https://cdn.simpleicons.org/react/61DAFB",
  },
  {
    title: "UI/UX Design",
    text: "User-centred mobile interfaces designed for engagement and retention.",
    iconUrl: "https://cdn.simpleicons.org/figma/F24E1E",
  },
  {
    title: "App Maintenance",
    text: "Ongoing updates, bug fixes, and performance tuning for live apps.",
    iconUrl: "https://cdn.simpleicons.org/vercel/111111",
  },
  {
    title: "App Consulting",
    text: "Strategic guidance on architecture, tech stack, and go-to-market planning.",
    iconUrl: "https://cdn.simpleicons.org/googlecloud/4285F4",
  },
];

const techStack: TechItem[] = [
  { name: "Flutter", logo: "https://cdn.simpleicons.org/flutter/02569B", group: "Frontend" },
  { name: "React Native", logo: "https://cdn.simpleicons.org/react/61DAFB", group: "Frontend" },
  { name: "Swift", logo: "https://cdn.simpleicons.org/swift/F05138", group: "Frontend" },
  { name: "Kotlin", logo: "https://cdn.simpleicons.org/kotlin/7F52FF", group: "Frontend" },
  { name: "Node.js", logo: "https://cdn.simpleicons.org/nodedotjs/5FA04E", group: "Backend" },
  { name: "Laravel", logo: "https://cdn.simpleicons.org/laravel/FF2D20", group: "Backend" },
  { name: "Firebase", logo: "https://cdn.simpleicons.org/firebase/DD2C00", group: "Backend" },
  { name: ".NET", logo: "https://cdn.simpleicons.org/dotnet/512BD4", group: "Backend" },
  { name: "MongoDB", logo: "https://cdn.simpleicons.org/mongodb/47A248", group: "Database" },
  { name: "PostgreSQL", logo: "https://cdn.simpleicons.org/postgresql/4169E1", group: "Database" },
  { name: "MySQL", logo: "https://cdn.simpleicons.org/mysql/4479A1", group: "Database" },
  { name: "AWS", logo: "https://cdn.simpleicons.org/amazonwebservices/232F3E", group: "Cloud" },
  { name: "Azure", logo: "https://cdn.simpleicons.org/microsoftazure/0078D4", group: "Cloud" },
  { name: "Google Cloud", logo: "https://cdn.simpleicons.org/googlecloud/4285F4", group: "Cloud" },
];

const whyChoose: CardItem[] = [
  { title: "Experienced Team", text: "Senior mobile engineers with proven delivery across industries.", icon: "ET" },
  { title: "Agile Development", text: "Iterative sprints with transparent progress and fast feedback loops.", icon: "AD" },
  { title: "Quality Assurance", text: "Rigorous testing across devices, OS versions, and edge cases.", icon: "QA" },
  { title: "Timely Delivery", text: "Structured timelines with milestone-based releases you can trust.", icon: "TD" },
  { title: "Scalable Architecture", text: "Future-proof codebases built to grow with your user base.", icon: "SA" },
  { title: "Secure Applications", text: "Security-first development with encryption and compliance best practices.", icon: "SE" },
  { title: "Transparent Communication", text: "Dedicated project updates, demos, and direct access to your team.", icon: "TC" },
  { title: "Affordable Pricing", text: "Enterprise-grade quality at competitive, transparent pricing.", icon: "AP" },
];

const processSteps: CardItem[] = [
  { title: "Discovery", text: "Understanding goals, users, and requirements", icon: "01" },
  { title: "Strategy", text: "Roadmap, tech stack, and feature prioritization", icon: "02" },
  { title: "UI/UX Design", text: "Wireframes, prototypes, and visual design", icon: "03" },
  { title: "Development", text: "Agile build with regular sprint demos", icon: "04" },
  { title: "Testing", text: "QA, performance, and device compatibility", icon: "05" },
  { title: "Deployment", text: "App Store and Play Store launch", icon: "06" },
  { title: "Support & Maintenance", text: "Updates, monitoring, and ongoing improvements", icon: "07" },
];

const industries: CardItem[] = [
  { title: "Healthcare", icon: "HC", iconUrl: "https://cdn.simpleicons.org/healthicons/0EA5E9" },
  { title: "Ecommerce", icon: "EC", iconUrl: "https://cdn.simpleicons.org/shopify/7AB55C" },
  { title: "Education", icon: "ED", iconUrl: "https://cdn.simpleicons.org/googleclassroom/0F9D58" },
  { title: "Finance", icon: "FN", iconUrl: "https://cdn.simpleicons.org/stripe/635BFF" },
  { title: "Travel", icon: "TR", iconUrl: "https://cdn.simpleicons.org/airbnb/FF5A5F" },
  { title: "Real Estate", icon: "RE", iconUrl: "https://cdn.simpleicons.org/zillow/006AFF" },
  { title: "Logistics", icon: "LG", iconUrl: "https://cdn.simpleicons.org/fedex/4D148C" },
  { title: "Food Delivery", icon: "FD", iconUrl: "https://cdn.simpleicons.org/ubereats/06C167" },
  { title: "Fitness", icon: "FT", iconUrl: "https://cdn.simpleicons.org/strava/FC4C02" },
  { title: "Social Networking", icon: "SN", iconUrl: "https://cdn.simpleicons.org/instagram/E4405F" },
];

const appFeatures: CardItem[] = [
  { title: "Push Notifications", icon: "PN" },
  { title: "Real-Time Chat", icon: "RC" },
  { title: "GPS Tracking", icon: "GP" },
  { title: "Payment Integration", icon: "PI" },
  { title: "Social Login", icon: "SL" },
  { title: "Analytics Dashboard", icon: "AD" },
  { title: "Multi-language Support", icon: "ML" },
  { title: "Cloud Sync", icon: "CS" },
  { title: "Offline Mode", icon: "OF" },
  { title: "Security Layer", icon: "SC" },
  { title: "API Integration", icon: "API" },
  { title: "Admin Panel", icon: "AP" },
];

const caseStudies = [
  { title: "Food Delivery App", metric: "+300%", label: "User Growth", bars: [38, 52, 70, 85, 98] },
  { title: "Healthcare App", metric: "100K+", label: "Downloads", bars: [30, 45, 62, 78, 95] },
  { title: "Ecommerce App", metric: "+250%", label: "Revenue Increase", bars: [42, 58, 66, 82, 96] },
];

const testimonials = [
  {
    name: "Arjun Patel",
    company: "FoodTech Startup",
    review: "WebTech delivered our food delivery app on time with exceptional UI. User engagement doubled within months.",
    avatar: "AP",
  },
  {
    name: "Sarah Johnson",
    company: "HealthCare Plus",
    review: "Their team understood our healthcare compliance needs perfectly. The app is secure, fast, and loved by patients.",
    avatar: "SJ",
  },
  {
    name: "Michael Chen",
    company: "RetailMax",
    review: "From concept to App Store launch in 12 weeks. Professional, responsive, and highly skilled developers.",
    avatar: "MC",
  },
];

const showcaseApps = [
  { title: "Mobile Screens", type: "mobile" },
  { title: "Dashboard UI", type: "dashboard" },
  { title: "Ecommerce App", type: "ecommerce" },
  { title: "Healthcare App", type: "healthcare" },
  { title: "Booking App", type: "booking" },
];

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AnimatedSection: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = "", children }) => (
  <motion.section
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-90px" }}
    variants={fadeUp}
    transition={{ duration: 0.65, ease: "easeOut" }}
  >
    {children}
  </motion.section>
);

const SectionHeader: React.FC<{
  eyebrow?: string;
  title: string;
  text?: string;
  light?: boolean;
}> = ({ eyebrow, title, text, light = false }) => (
  <div className={`map-section-header ${light ? "map-section-header--light" : ""}`}>
    {eyebrow && <span>{eyebrow}</span>}
    <h2>{title}</h2>
    {text && <p>{text}</p>}
  </div>
);

const HeroPhones = () => (
  <motion.div
    className="map-phones"
    animate={{ y: [0, -12, 0] }}
    transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
  >
    <div className="map-phone map-phone--ios">
      <div className="map-phone__notch" />
      <div className="map-phone__screen">
        <div className="map-phone__stat">
          <span>Revenue</span>
          <strong>$42.8K</strong>
        </div>
        <div className="map-phone__chart">
          {[40, 55, 48, 72, 65, 88].map((h, i) => (
            <span key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="map-phone__cards">
          <div />
          <div />
        </div>
      </div>
    </div>

    <div className="map-phone map-phone--android">
      <div className="map-phone__screen">
        <div className="map-phone__stat">
          <span>Active Users</span>
          <strong>18.4K</strong>
        </div>
        <div className="map-phone__line-chart" />
        <div className="map-phone__list">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>

    <div className="map-float-icon map-float-icon--apple">
      <img src="https://cdn.simpleicons.org/apple/FFFFFF" alt="" aria-hidden="true" />
    </div>
    <div className="map-float-icon map-float-icon--android">
      <img src="https://cdn.simpleicons.org/android/3DDC84" alt="" aria-hidden="true" />
    </div>
    <div className="map-float-pill">
      <span>Downloads</span>
      <strong>+68%</strong>
    </div>
  </motion.div>
);

const MobileAppDevelopmentPage: React.FC = () => {
  useEffect(() => {
    document.title =
      "Mobile App Development That Drives Business Growth | WebTech Bangalore";
    const description =
      "Build scalable, high-performance iOS and Android apps with WebTech's premium mobile app development services in Bangalore.";
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, []);

  return (
    <Fragment>
      <div className="map-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            {/* Hero */}
            <section className="map-hero" aria-labelledby="mobile-hero-title">
              <div className="map-hero__mesh" aria-hidden="true" />
              <div className="map-particles" aria-hidden="true">
                {[...Array(16)].map((_, i) => (
                  <span key={i} />
                ))}
              </div>

              <div className="map-container map-hero__grid">
                <motion.div
                  className="map-hero__content"
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  transition={{ duration: 0.75, ease: "easeOut" }}
                >
                  <span className="map-eyebrow">Top Mobile App Development Company</span>
                  <h1 id="mobile-hero-title">
                    Mobile App Development That <span>Drives Business Growth</span>
                  </h1>
                  <p>
                    We build scalable, high-performance mobile applications for
                    startups, enterprises, and growing businesses.
                  </p>
                  <div className="map-hero__actions">
                    <Link className="map-btn map-btn--primary" to="/contact">
                      Get Free Consultation
                    </Link>
                    <Link className="map-btn map-btn--secondary" to="/project">
                      View Portfolio
                    </Link>
                  </div>
                  <div className="map-trust-row">
                    {trustStats.map((stat) => (
                      <div className="map-trust-item" key={stat.label}>
                        <strong>{stat.value}</strong>
                        <span>{stat.label}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <div className="map-hero__visual">
                  <HeroPhones />
                </div>
              </div>
            </section>

            {/* Services */}
            <AnimatedSection className="map-section map-section--white">
              <div className="map-container">
                <SectionHeader
                  title="Our Mobile App Development Services"
                  light
                />
                <motion.div className="map-service-grid" variants={stagger}>
                  {services.map((item) => (
                    <motion.article className="map-service-card" variants={fadeUp} key={item.title}>
                      <div className="map-service-card__icon">
                        {item.iconUrl && (
                          <img src={item.iconUrl} alt="" loading="lazy" aria-hidden="true" />
                        )}
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Tech Stack Carousel */}
            <AnimatedSection className="map-section map-section--dark">
              <div className="map-container">
                <SectionHeader title="Technologies Powering Our Apps" />
              </div>
              <Marquee speed={35} gradient={false} pauseOnHover>
                <div className="map-tech-marquee">
                  {techStack.map((tech) => (
                    <div className="map-tech-card" key={tech.name}>
                      <img src={tech.logo} alt={`${tech.name} logo`} loading="lazy" />
                      <span>{tech.name}</span>
                      {tech.group && <small>{tech.group}</small>}
                    </div>
                  ))}
                </div>
              </Marquee>
            </AnimatedSection>

            {/* Why Choose */}
            <AnimatedSection className="map-section">
              <div className="map-container">
                <SectionHeader title="Why Choose WebTech?" />
                <motion.div className="map-why-grid" variants={stagger}>
                  {whyChoose.map((item) => (
                    <motion.article className="map-glass-card" variants={fadeUp} key={item.title}>
                      <div className="map-glass-card__icon">{item.icon}</div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            {/* Process */}
            <AnimatedSection className="map-section map-section--white">
              <div className="map-container">
                <SectionHeader title="Our App Development Process" light />
                <div className="map-timeline">
                  {processSteps.map((step) => (
                    <article className="map-timeline__item" key={step.title}>
                      <div className="map-timeline__icon">{step.icon}</div>
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* Industries */}
            <AnimatedSection className="map-section map-section--light">
              <div className="map-container">
                <SectionHeader title="Industries We Serve" light />
                <div className="map-industry-grid">
                  {industries.map((ind) => (
                    <article className="map-industry-card" key={ind.title}>
                      <div className="map-industry-card__icon">
                        {ind.iconUrl ? (
                          <img src={ind.iconUrl} alt="" loading="lazy" aria-hidden="true" />
                        ) : (
                          ind.icon
                        )}
                      </div>
                      <h3>{ind.title}</h3>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* Features */}
            <AnimatedSection className="map-section">
              <div className="map-container">
                <SectionHeader title="Powerful Features We Build" />
                <div className="map-feature-grid">
                  {appFeatures.map((f) => (
                    <article className="map-feature-card" key={f.title}>
                      <CheckIcon />
                      <h3>{f.title}</h3>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* Case Studies */}
            <AnimatedSection className="map-section map-section--compact">
              <div className="map-container">
                <SectionHeader title="Success Stories" />
                <div className="map-story-grid">
                  {caseStudies.map((story) => (
                    <article className="map-story-card" key={story.title}>
                      <h3>{story.title}</h3>
                      <strong>{story.metric}</strong>
                      <p>{story.label}</p>
                      <div className="map-story-chart" aria-hidden="true">
                        {story.bars.map((bar, i) => (
                          <span key={i} style={{ height: `${bar}%` }} />
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* Testimonials */}
            <AnimatedSection className="map-section map-section--white">
              <div className="map-container">
                <SectionHeader title="What Our Clients Say" light />
                <div className="map-testimonial-row">
                  {testimonials.map((t) => (
                    <article className="map-testimonial-card" key={t.name}>
                      <div className="map-testimonial-card__head">
                        <div className="map-avatar">{t.avatar}</div>
                        <div>
                          <h3>{t.name}</h3>
                          <span>{t.company}</span>
                        </div>
                      </div>
                      <div className="map-rating" aria-label="5 out of 5 rating">
                        5.0 / 5
                      </div>
                      <p>{t.review}</p>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* App Showcase */}
            <AnimatedSection className="map-section">
              <div className="map-container">
                <SectionHeader title="App Showcase" />
                <div className="map-showcase-grid">
                  {showcaseApps.map((app) => (
                    <article className={`map-showcase-card map-showcase-card--${app.type}`} key={app.title}>
                      <div className="map-showcase-card__device" aria-hidden="true">
                        <div className="map-showcase-card__screen" />
                      </div>
                      <h3>{app.title}</h3>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            {/* CTA */}
            <AnimatedSection className="map-cta">
              <div className="map-container map-cta__inner">
                <div>
                  <span>Ready to Launch?</span>
                  <h2>Ready To Build Your Next Mobile App?</h2>
                  <p>
                    Let&apos;s turn your idea into a powerful mobile experience.
                  </p>
                </div>
                <Link className="map-btn map-btn--primary" to="/contact">
                  Start Your Project
                </Link>
              </div>
            </AnimatedSection>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default MobileAppDevelopmentPage;
